import { CalendarDays, Landmark, Percent, Wallet } from "lucide-react";
import { EMPTY, formatDate, formatField, formatMoney, slug } from "../../lib/format";
import { StatusBadge } from "./StatusBadge";

const Highlight = ({ icon: Icon, label, sub, value, title, testId }) => (
  <div className="flex min-w-0 items-center gap-2.5 rounded-[12px] bg-[#f5f8fc] px-3 py-2.5" title={title} data-testid={`${testId}-tile`}>
    <Icon className="h-4 w-4 shrink-0 text-[#0f5ef7]" />
    <div className="min-w-0 leading-tight">
      <div className="text-[11px] font-semibold text-[#5b667a]">
        {label}
        {sub && <span className="font-medium text-[#8a94a6]" data-testid={`${testId}-sub`}> {sub}</span>}
      </div>
      <div className="truncate text-sm font-extrabold text-[#182235]" data-testid={testId}>{value}</div>
    </div>
  </div>
);

const saldoProps = (loan, currency) =>
  loan.saldo === null || loan.saldo === undefined
    ? { value: EMPTY, title: "Sin datos de pagos" }
    : {
        value: formatMoney(loan.saldo, currency),
        sub: `al ${formatDate(loan.cut_date)}`,
        title: `Monto menos capital pagado según la hoja de pagos del Excel, al ${formatDate(loan.cut_date)}`,
      };

export const LoanCard = ({ loan }) => {
  const byKey = Object.fromEntries(loan.fields.map((f) => [f.key, f]));
  const currency = byKey["Currency"]?.value;
  const id = slug(loan.loan_id);
  const fmt = (k) => (byKey[k] ? formatField(byKey[k], currency) : EMPTY);
  return (
    <article className="tr-card overflow-hidden" data-testid={`loan-card-${id}`}>
      <header className="space-y-4 border-b border-[#e8edf5] p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="tr-eyebrow">Crédito</div>
            <h3 className="font-mono text-base font-bold text-[#182235]" data-testid={`loan-id-${id}`}>{loan.loan_id}</h3>
          </div>
          <div className="flex flex-wrap items-start gap-5">
            <div>
              <div className="text-[11px] font-semibold text-[#5b667a]">Estatus</div>
              <StatusBadge status={loan.status} testId={`loan-status-${id}`} />
            </div>
            <div>
              <div className="text-[11px] font-semibold text-[#5b667a]">Fecha de vencimiento</div>
              <div className="text-sm font-extrabold text-[#182235]" data-testid={`loan-maturity-${id}`}>{formatDate(loan.maturity)}</div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Highlight icon={Landmark} label="Monto" value={fmt("Loan Amount")} testId={`loan-amount-${id}`} />
          <Highlight icon={Wallet} label="Saldo insoluto" testId={`loan-saldo-${id}`} {...saldoProps(loan, currency)} />
          <Highlight icon={CalendarDays} label="Disposición" value={fmt("Disbursement Date")} testId={`loan-date-${id}`} />
          <Highlight icon={Percent} label="Tasa efectiva" value={fmt("Effective Interest Rate")} testId={`loan-rate-${id}`} />
        </div>
      </header>
      <dl className="grid grid-cols-1 gap-x-5 gap-y-3 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-4">
        {loan.fields.map((f) => (
          <div key={f.key} className="min-w-0 border-b border-[#eef2f7] pb-2" data-testid={`loan-${id}-field-${slug(f.key)}`}>
            <dt className="text-xs font-semibold text-[#5b667a]" title={f.hint || f.key} data-testid={`loan-${id}-label-${slug(f.key)}`}>{f.label}</dt>
            <dd className="break-words text-sm font-semibold text-[#182235]">{formatField(f, currency)}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
};
